import { useJSONFetcher, JSONFetcherOptions } from "@outplayed/json-fetcher";
import { getApiRoot } from "@ugg/shared/api/api-helpers";
import { useUGGApiVersions } from "@ugg/shared/api/requests/ugg-api-versions";
import { useValidateQueryParams } from "@ugg/shared/query-params/params-helpers";
import { validParams } from "@ugg/shared/query-params/valid-params";
import { TIER_LIST_PAGES } from "@ugg/shared/pages/tier-list-pages";
import { QueueTypeS } from "@ugg/shared/utils/queue-type-helpers";
import { RankS } from "@ugg/shared/utils/rank-helpers";

// Data Parsers
import { cleanCombat } from "@ugg/shared/api/data-parser/tier-lists/combat-tier-list";

// Interfaces
import { CombatTierList } from "@ugg/shared/interfaces/tier-lists/combat-tier-list.interface";

// ------------------------------------------------------------------------------------------------------------

interface TierListParams {
  region: string;
  patch: string;
  queueType: string;
  rank: string;
}

// ------------------------------------------------------------------------------------------------------------
// COMBAT TIER LIST
// ------------------------------------------------------------------------------------------------------------

function useCombatURL(params: TierListParams) {
  const { data: version } = useUGGApiVersions({
    apiKey: "combat",
    patch: params.patch,
  });

  if (
    params.queueType === QueueTypeS.ARAM ||
    params.queueType === QueueTypeS.URF ||
    params.queueType === QueueTypeS.ARURF ||
    params.queueType === QueueTypeS.NEXUS_BLITZ ||
    params.queueType === QueueTypeS.NORMAL_BLIND ||
    params.queueType === QueueTypeS.NORMAL_DRAFT ||
    params.queueType === QueueTypeS.QUICKPLAY ||
    params.queueType === QueueTypeS.SWIFTPLAY ||
    params.queueType === QueueTypeS.ONE_FOR_ALL
  ) {
    params.rank = RankS.OVERALL;
  }

  const root = getApiRoot(params.patch);
  return [`${root}/table/combat`, params.region, params.patch, params.queueType, params.rank, `${version}.json`].join("/");
}

export function useCombatTierList(
  options?: { params?: Record<string, any> } & JSONFetcherOptions<CombatTierList, ReturnType<typeof cleanCombat>>,
) {
  const { params = {}, ssr = false, skip = false } = options || {};
  const validateQueryParams = useValidateQueryParams(validParams);
  const validatedQueryParams = validateQueryParams(TIER_LIST_PAGES.COMBAT_TIER_LIST, params, true);

  const url = useCombatURL(validatedQueryParams);
  return useJSONFetcher<CombatTierList, ReturnType<typeof cleanCombat>>(url, {
    skip,
    ssr,
    onCompleted: (url, json) => cleanCombat(json),
  });
}
